// Forced first-login password change. Reached when /auth/me or login
// reports must_change_password=true (manual-credential managers).
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { toast } from "sonner";
import useAuthStore from "../store/authStore";
import { Loader2, ShieldCheck } from "lucide-react";

const ORANGE = "#FF6B00";

export default function ForcePasswordChange() {
  const navigate = useNavigate();
  const { checkAuth } = useAuthStore();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (next !== confirm) { toast.error("Passwords do not match"); return; }
    if (next === current) { toast.error("New password must be different"); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated. Welcome aboard!");
      await checkAuth();
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not change password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4" data-testid="force-password-change-page">
      <div className="bg-white border border-slate-200 rounded-2xl p-8 max-w-md w-full shadow-sm">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="h-6 w-6" style={{ color: ORANGE }} />
          <h1 className="text-2xl font-bold text-slate-900">Set a new password</h1>
        </div>
        <p className="text-slate-500 text-sm mb-6">For security, you must change your temporary password before continuing.</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700">Current (temporary) password</label>
            <input data-testid="fpc-current" type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
                   className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 focus:border-orange-400 focus:outline-none" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">New password</label>
            <input data-testid="fpc-new" type="password" value={next} onChange={(e) => setNext(e.target.value)}
                   className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 focus:border-orange-400 focus:outline-none"
                   placeholder="At least 8 characters" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Confirm new password</label>
            <input data-testid="fpc-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                   className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 focus:border-orange-400 focus:outline-none" />
          </div>
          <button type="submit" disabled={loading} data-testid="fpc-submit"
                  className="w-full text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2"
                  style={{ background: ORANGE }}>
            {loading ? <Loader2 className="animate-spin h-4 w-4" /> : "Update password"}
          </button>
        </form>
      </div>
    </div>
  );
}
